import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';

const STATUS_BADGE_INFO = {
  erfolgreich: { cls: 'bg-teal-50 text-teal-700', icon: '✓' },
  eingeschraenkt: { cls: 'bg-amber-50 text-amber-700', icon: '⚠' },
  fehlgeschlagen: { cls: 'bg-red-50 text-red-700', icon: '✗' },
  aktiv: { cls: 'bg-teal-50 text-teal-700', icon: '✓', label: 'Aktiv' },
  nicht_aktiv: { cls: 'bg-red-50 text-red-700', icon: '✗', label: 'Nicht aktiv' },
  nicht_geprueft: { cls: 'bg-gray-100 text-gray-500', icon: '–', label: 'Nicht geprüft' },
};
const statusBadge = (status) => {
  const s = STATUS_BADGE_INFO[status] || STATUS_BADGE_INFO.fehlgeschlagen;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
      {s.icon} {s.label || status}
    </span>
  );
};

const InfoRow = ({ label, value }) => (
  <div>
    <div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div>
    <div className="text-sm font-medium text-gray-900 mt-0.5">{value || '—'}</div>
  </div>
);

export default function DRReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handlePrint = () => {
    const token = localStorage.getItem('accessToken');
    const url = `/api/dr-reports/${id}/print?token=${encodeURIComponent(token)}`;
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 2000);
      }, 500);
    };
  };

  useEffect(() => {
    api.get(`/dr-reports/${id}`)
      .then(r => { setReport(r.data); setSendEmail(r.data.email_service || ''); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '—';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString('de-DE') : '—';

  const handleSend = async () => {
    if (!sendEmail) { setSendError('Bitte E-Mail-Adresse eingeben.'); return; }
    setSending(true); setSendError(''); setSendSuccess('');
    try {
      await api.post(`/dr-reports/${id}/send`, { email: sendEmail });
      setSendSuccess(`Bericht erfolgreich gesendet an ${sendEmail}`);
      const r = await api.get(`/dr-reports/${id}`);
      setReport(r.data);
    } catch (err) {
      setSendError(err.response?.data?.error || 'Versand fehlgeschlagen.');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Bericht wirklich löschen?')) return;
    setDeleting(true);
    try {
      await api.delete(`/dr-reports/${id}`);
      navigate('/dr-reports');
    } catch (err) {
      alert(err.response?.data?.error || 'Löschen fehlgeschlagen.');
      setDeleting(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-gray-500">Lädt...</div>;
  if (!report) return <div className="p-6 text-sm text-gray-500">Bericht nicht gefunden.</div>;

  const checks = [
    { label: 'Systemstart auf isolierter Testumgebung', value: report.check_system_start },
    { label: 'Anmeldung am wiederhergestellten System', value: report.check_login },
    { label: 'Dateiintegrität geprüft', value: report.check_file_integrity },
    { label: 'Anwendungen lauffähig', value: report.check_apps_running },
  ];

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/dr-reports')} className="text-gray-400 hover:text-gray-600 text-sm">← Zurück</button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-gray-900">{report.report_number}</h1>
          <p className="text-sm text-gray-500">{report.customer_name} · {formatDate(report.test_date)}</p>
        </div>
        <button onClick={handlePrint}
            className="border border-teal-200 text-teal-600 hover:bg-teal-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            ↓ PDF
          </button>
        {!report.sent_at && (
          <button onClick={() => navigate(`/dr-reports/${id}/edit`)}
            className="border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            Bearbeiten
          </button>
        )}
        {!report.sent_at && (
          <button onClick={handleDelete} disabled={deleting}
            className="border border-red-200 text-red-500 hover:bg-red-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            Löschen
          </button>
        )}
      </div>

      <div className="flex flex-col gap-4">

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4 pb-2 border-b border-gray-100">Kundendaten & Gerät</h2>
          <div className="grid grid-cols-3 gap-4">
            <InfoRow label="Kunde" value={report.customer_name} />
            <InfoRow label="Gerät" value={report.device_name} />
            <InfoRow label="Bezeichnung" value={report.device_label} />
            <InfoRow label="Gerätetyp" value={report.device_type} />
            <InfoRow label="Betriebssystem" value={report.os_name} />
            <InfoRow label="Backup vom" value={formatDate(report.backup_date)} />
            <InfoRow label="Sicherungssoftware" value={report.backup_software || '—'} />
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">Ransomware-Schutz</div>
              <div className="mt-0.5">{statusBadge(report.ransomware_protection || 'nicht_geprueft')}</div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4 pb-2 border-b border-gray-100">Übungsdetails</h2>
          <div className="grid grid-cols-3 gap-4">
            <InfoRow label="Datum" value={formatDate(report.test_date)} />
            <InfoRow label="Uhrzeit" value={report.test_time ? report.test_time + ' Uhr' : '—'} />
            <InfoRow label="Dauer" value={report.test_duration ? report.test_duration + ' Std.' : '—'} />
            <InfoRow label="Testmethode" value={report.testmethod} />
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">Gesamtergebnis</div>
              <div className="mt-0.5">{statusBadge(report.test_status)}</div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3 pb-2 border-b border-gray-100">Testergebnisse</h2>
          <table className="w-full text-sm">
            <tbody>
              {checks.map(c => (
                <tr key={c.label} className="border-b border-gray-50 last:border-0">
                  <td className="py-2 text-gray-700">{c.label}</td>
                  <td className="py-2 text-right">{statusBadge(c.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {report.notes && (
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3 pb-2 border-b border-gray-100">Hinweise</h2>
            <p className="text-sm text-gray-600 leading-relaxed">{report.notes}</p>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4 pb-2 border-b border-gray-100">Bericht versenden</h2>
          {report.sent_at ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-teal-600">✓ Versendet am {formatDateTime(report.sent_at)} an {report.sent_to_email}</p>
              <div className="flex gap-2 mt-1">
                <input type="email" value={sendEmail} onChange={e => setSendEmail(e.target.value)}
                  placeholder="E-Mail-Adresse"
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 flex-1" />
                <button onClick={handleSend} disabled={sending}
                  className="border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                  {sending ? 'Sendet...' : 'Erneut senden'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex gap-2">
                <input type="email" value={sendEmail} onChange={e => setSendEmail(e.target.value)}
                  placeholder="E-Mail-Adresse des Kunden"
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 flex-1" />
                <button onClick={handleSend} disabled={sending}
                  className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors">
                  {sending ? 'Sendet...' : 'Bericht senden'}
                </button>
              </div>
              {sendError && <p className="text-sm text-red-600">{sendError}</p>}
              {sendSuccess && <p className="text-sm text-teal-600">{sendSuccess}</p>}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
